import {
  Button,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import IosShareIcon from '@mui/icons-material/IosShare';
import { AssessmentApi } from 'lib/api/assessment';
import React from 'react';
import { CompanyApi } from 'lib/api/company';
import { ICompany } from 'modules/companies/components/CompanyForm/models';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';
import { Visibility } from '@mui/icons-material';
import CustomizedTable from './assesstment-list-table';
import { useSnackbar } from 'notistack';
import { InstrumentApi } from 'lib/api/instrument';
import { InstrumentDTO } from 'lib/api/instrument.protocols';
import CardBox from 'lib/components/CardBox';
import {
  AssessmentListType,
  ITableBody,
  OpenViewDetailsPropTypes,
} from './types';
import { FileApi } from 'lib/api/file';
import { MessagingApi, messageBodyProps } from 'lib/api/messaging';
import { BaseExportExcelContainer } from '../../sectors/pages/list/style';
import Tooltip from '@mui/material/Tooltip';
import { cpfMask } from '../../../lib/helpers/masks';
import { useWhatsappPolling } from '../../../lib/whatsapp/useWhatsappPolling';
import { WhatsappSendButton } from '../../../lib/whatsapp/WhatsappSendButton';
import { WhatsappStatusLegend } from '../../../lib/whatsapp/WhatsappStatusLegend';
import { useState } from 'react';
dayjs.locale('pt-br');

export const AssessmentListPage = ({
  onViewTest,
}: {
  onViewTest: ({
    assessmentResponse,
    userInfo,
    company,
    instrument,
    region,
    costCenter,
    status,
  }: OpenViewDetailsPropTypes) => void;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const [companies, setCompanies] = useState<ICompany[]>();
  const [instruments, setInstruments] = useState<InstrumentDTO[]>([]);
  const [tableBody, setTableBody] = useState<ITableBody[]>([]);
  const tableHead = ['Empresa', 'Candidato', 'Status', 'Cobrança', ''];

  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState<number | undefined>(undefined);
  const [excelLoading, setExcelLoading] = useState(false);
  const lastAssessmentEntitiesRef = React.useRef<
    AssessmentListType['entities']
  >([]);

  const {
    isMountedRef,
    sendingWhatsapp,
    whatsappStatuses,
    setWhatsappStatuses,
    pollWhatsappStatus,
    startSending,
    stopSending,
  } = useWhatsappPolling();

  const candidateName = searchParams.get('candidateName') || '';
  const companyName = searchParams.get('companyName') || '';
  const requestDate = searchParams.get('requestDate') || '';
  const instrumentName = searchParams.get('instrumentName') || '';
  const candidateCpf = searchParams.get('candidateCpf') || '';
  const statusFilter = searchParams.get('status') || '';
  const queryString = `assessment=true&candidateName=${encodeURIComponent(
    candidateName,
  )}&companyName=${
    encodeURIComponent(companyName) === 'todas'
      ? ''
      : encodeURIComponent(companyName)
  }&requestDate=${encodeURIComponent(
    requestDate,
  )}&instrumentName=${encodeURIComponent(
    instrumentName || '',
  )}&candidateCpf=${encodeURIComponent(
    candidateCpf,
  )}&status=${encodeURIComponent(statusFilter)}`;

  const searchParamsObject = Object.fromEntries(searchParams.entries());

  // TODO: extrair updateSearchParams e updateOnlyCandidateCpfParam para src/lib/hooks/useSearchParamsSync.ts (idêntico em consultant-request-list-page)
  const updateSearchParams = (param: string, value: string) => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (param === 'candidateCpf') {
      newSearchParams.set(param, cpfMask(value));
    } else {
      newSearchParams.set(param, value);
    }
    setSearchParams(newSearchParams);
  };

  const updateOnlyCandidateCpfParam = () => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (
      searchParamsObject['candidateCpf'] &&
      searchParamsObject['candidateCpf'].length > 0
    ) {
      newSearchParams.set(
        'candidateCpf',
        cpfMask(searchParamsObject['candidateCpf']),
      );
      setSearchParams(newSearchParams);
    }
  };

  const fetchData = async (page: number) => {
    try {
      const [assesmentList, companiesList, instrumentsList] = await Promise.all(
        [
          AssessmentApi.getSearchedAssessments(queryString, page),
          CompanyApi.getCompanies(),
          InstrumentApi.getAssessmentInstruments(),
        ],
      );
      setPageCount(assesmentList.meta.pageCount);
      setCompanies(companiesList);
      setInstruments(instrumentsList);

      const entities = assesmentList.entities as AssessmentListType['entities'];
      lastAssessmentEntitiesRef.current = entities;
      mountTableBody(entities, {}, {});

      const ids = entities.map((item) => item.id);
      const batchResult = await MessagingApi.getWhatsappStatusBatch(ids);

      const newStatuses: Record<
        string,
        { status: string | null; updatedAt: string | null }
      > = {};
      ids.forEach((id) => {
        const key = `${id}:link-avaliacao`;
        newStatuses[key] = {
          status: batchResult[key]?.messageStatus ?? null,
          updatedAt: batchResult[key]?.updatedAt ?? null,
        };
      });

      if (isMountedRef.current) {
        setWhatsappStatuses(newStatuses);
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Erro ao buscar as provas', { variant: 'error' });
    }
  };

  // TODO: extrair para src/lib/hooks/useExcelExport.ts (idêntico em consultant-request-list-page, diferindo apenas na chamada FileApi)
  const getExportRequestsLink = async () => {
    setExcelLoading(true);
    try {
      const url = await FileApi.exportAssessments();
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', url);
      document.body.appendChild(link);
      link.click();
    } catch (e) {
      setExcelLoading(false);
      console.error(e);
      enqueueSnackbar('Erro ao exportar as provas', { variant: 'error' });
    } finally {
      setExcelLoading(false);
    }
  };

  const handleWhatsappMsgSender = async (body: messageBodyProps) => {
    const requestId = body.analystRequestId;
    startSending(requestId);
    try {
      const sent = await MessagingApi.sendMessage(body);
      enqueueSnackbar(
        'Mensagem enviada. Aguardando confirmação da operadora...',
        {
          variant: 'info',
          autoHideDuration: 5000,
        },
      );
      await pollWhatsappStatus(
        requestId,
        'link-avaliacao',
        body.candidateWhatsapp,
        sent?.messageSid,
      );
    } catch (err: any) {
      enqueueSnackbar(
        err?.response?.data?.message ||
          'Erro ao enviar mensagem. Tente novamente ou acione o suporte.',
        { variant: 'error', autoHideDuration: 10000 },
      );
    } finally {
      if (isMountedRef.current) {
        stopSending(requestId);
      }
    }
  };

  const mountTableBody = (
    assessmentList: AssessmentListType['entities'],
    sending: Record<string, boolean> = {},
    statuses: Record<
      string,
      { status: string | null; updatedAt: string | null }
    > = {},
  ) => {
    const body: ITableBody[] = [];
    assessmentList?.forEach((item) => {
      const isSending = !!sending[item.id];
      const isDisabled =
        item.status === 'FINALIZADO' || item.status === 'CANCELADO';
      const statusKey = `${item.id}:link-avaliacao`;
      const whatsappStatus = statuses[statusKey]?.status ?? null;
      const whatsappUpdatedAt = statuses[statusKey]?.updatedAt ?? null;

      body.push({
        company: item.company.name,
        candidateName: item.candidate.name,
        status: item.status,
        whatsappButton: (
          <WhatsappSendButton
            label="Reenviar"
            status={whatsappStatus}
            updatedAt={whatsappUpdatedAt}
            isSending={isSending}
            isDisabled={isDisabled}
            onClick={() =>
              handleWhatsappMsgSender({
                analystRequestId: item.id,
                candidateName: item.candidate.name,
                candidateWhatsapp: item.candidate.whatsapp,
                companyName: item.company.name,
              })
            }
          />
        ),
        actions: (
          <div
            onClick={() =>
              onViewTest({
                assessmentResponse: item.assessmentResponse,
                userInfo: item.candidate,
                company: item.company,
                instrument: item.instrument,
                region: item.region,
                costCenter: item.costCenter,
                status: item.status,
                analyst: item.analyst,
                id: item.id,
                assessmentInfo: item.assessmentInfo,
                additionalNote: item.additionalNote,
              })
            }
          >
            <Visibility />
          </div>
        ),
      });
    });
    setTableBody(body);
  };

  React.useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  React.useEffect(() => {
    if (lastAssessmentEntitiesRef.current.length > 0) {
      mountTableBody(
        lastAssessmentEntitiesRef.current,
        sendingWhatsapp,
        whatsappStatuses,
      );
    }
  }, [sendingWhatsapp, whatsappStatuses]);

  React.useEffect(() => {
    updateOnlyCandidateCpfParam();
    fetchData(page);
  }, [queryString, page]);

  return (
    <Container>
      <CardBox style={{ margin: '60px 0' }}>
        <Stack
          spacing={2}
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb="60px"
        >
          <Typography variant="h3" fontWeight={700}>
            Provas MS
          </Typography>
        </Stack>
        <Stack
          sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}
        >
          <TextField
            onChange={(event) => {
              page !== 1 && setPage(1);
              updateSearchParams('candidateName', event.target.value);
            }}
            label="Nome do candidato"
            variant="outlined"
            value={searchParamsObject.candidateName}
          />

          <FormControl fullWidth>
            <InputLabel id="company-select-label">Buscar Empresa</InputLabel>
            <Select
              labelId="companies-select-label"
              id="companies-simple-select"
              label="Companies"
              onChange={(event) => {
                page !== 1 && setPage(1);
                updateSearchParams(
                  'companyName',
                  event.target.value === 'todas' ? '' : event.target.value,
                );
              }}
              value={searchParamsObject.companyName || 'todas'}
            >
              <MenuItem value="todas">Todas</MenuItem>
              {companies?.map((item) => (
                <MenuItem value={item.name} key={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              format="DD/MM/YYYY"
              label="Buscar Data"
              value={
                searchParamsObject?.requestDate?.length > 0
                  ? dayjs(requestDate)
                  : undefined
              }
              onChange={(newValue) => {
                let requestDate = '';
                if (!newValue || !dayjs(newValue).isValid()) {
                  requestDate = '';
                } else {
                  requestDate = dayjs(newValue).format('YYYY-MM-DD');
                }
                page !== 1 && setPage(1);
                updateSearchParams('requestDate', requestDate);
              }}
            />
          </LocalizationProvider>

          <FormControl fullWidth>
            <InputLabel id="instrument-select-label">
              Buscar Instrumento
            </InputLabel>
            <Select
              labelId="instrument-select-label"
              id="instrument-simple-select"
              label="Instrument"
              onChange={(event) => {
                page !== 1 && setPage(1);
                updateSearchParams(
                  'instrumentName',
                  event.target.value === 'todas' ? '' : event.target.value,
                );
              }}
              value={searchParamsObject.instrumentName || 'todas'}
            >
              <MenuItem value="todas">Todas</MenuItem>
              {instruments?.map((item) => (
                <MenuItem value={item.name} key={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            onChange={(event) => {
              page !== 1 && setPage(1);
              updateSearchParams('candidateCpf', event.target.value);
            }}
            label="CPF do candidato"
            variant="outlined"
            value={cpfMask(searchParamsObject.candidateCpf)}
          />

          <FormControl fullWidth>
            <InputLabel id="status-select-label">Buscar Status</InputLabel>
            <Select
              labelId="status-select-label"
              id="status-simple-select"
              label="Buscar Status"
              onChange={(event) => {
                page !== 1 && setPage(1);
                updateSearchParams(
                  'status',
                  event.target.value === 'todos' ? '' : event.target.value,
                );
              }}
              value={searchParamsObject.status || 'todos'}
            >
              <MenuItem value="todos">Todos</MenuItem>
              <MenuItem value="EM ANDAMENTO">Em andamento</MenuItem>
              <MenuItem value="FINALIZADO">Finalizado</MenuItem>
              <MenuItem value="CANCELADO">Cancelado</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </CardBox>

      <BaseExportExcelContainer>
        <WhatsappStatusLegend />
        <div>
          <Tooltip title="Exportar em excel todos os registros das provas">
            <Button
              variant="contained"
              startIcon={
                excelLoading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : (
                  <IosShareIcon />
                )
              }
              onClick={() => getExportRequestsLink()}
              disabled={excelLoading}
            >
              Exportar em excel
            </Button>
          </Tooltip>
        </div>
      </BaseExportExcelContainer>

      <CustomizedTable tableHead={tableHead} tableBody={tableBody} />
      <Stack spacing={2} alignItems={'center'}>
        <Pagination
          count={pageCount}
          page={page}
          onChange={(e, value) => setPage(value)}
          defaultPage={page}
        />
      </Stack>
    </Container>
  );
};
